// Enhanced GCC Transformation Framework with Complete Value Leakage Integration
export const GCC_TRANSFORMATION_FRAMEWORK = {
  stages: {
    0: {
      id: 0,
      name: "Strategic Conception & Validation",
      duration: "Months 1-4",
      purpose: "Validate GCC relevance and build executive conviction",
      totalValueAtStake: "$50-200M+ recoverable value",
      phases: {
        0.1: {
          name: "Opportunity Discovery & Value Leakage Assessment",
          subSteps: {
            '0.1.1': {
              name: "Comprehensive Value Leakage Diagnostic",
              trigger: "Executive mandate for transformation",
              activity: "Full-spectrum value leakage assessment across 18 categories with quantitative modeling",
              output: "Prioritized value leakage portfolio with $50-200M+ identified recovery potential",
              dependencies: [],
              estimatedDuration: "4 weeks",
              risks: ["Underestimation of strategic leakage", "Political resistance to findings", "Data accessibility issues"],
              valueLeakageAnalysis: true,
              leakageCategories: ['financial', 'strategic', 'digital', 'ecosystem', 'operational', 'humanCapital', 'customer', 'compliance', 'innovation', 'talent', 'technology', 'process', 'data', 'supplyChain', 'quality', 'sustainability', 'governance', 'cultural', 'brand', 'strategicOptionality', 'quantum'],
              quantificationFramework: {
                directFinancial: {
                  duplicateSystems: "(Redundant licenses + Maintenance) * Systems count",
                  processInefficiencies: "(FTE hours wasted * loaded cost) + Opportunity cost",
                  revenueLeakage: "(Theoretical max revenue - Actual revenue) * Margin"
                },
                strategicImpact: {
                  slowInnovation: "Market share loss * margin impact",
                  talentTurnover: "(Recruitment + Training + Productivity loss) * turnover rate",
                  digitalTransformationDelay: "Competitive positioning loss + efficiency gap"
                }
              },
              dataCollection: {
                type: "comprehensive_leakage_assessment",
                fields: ["all_leakage_categories", "quantified_impact_range", "recovery_potential", "strategic_priority", "implementation_complexity"]
              }
            },
            '0.1.2': {
              name: "Strategic Intelligence & Market Position Analysis",
              trigger: "Initial leakage assessment complete",
              activity: "Competitive intelligence gaps analysis, market positioning assessment, technology radar development",
              output: "Strategic intelligence map with identified blind spots and positioning opportunities",
              dependencies: ['0.1.1'],
              estimatedDuration: "3 weeks",
              risks: ["Incomplete competitive data", "Market misread", "Technology trend misjudgment"],
              valueLeakageAnalysis: true,
              strategicLeakagePoints: {
                competitiveIntelligenceGaps: {
                  impact: "40-60% market share loss in 3-5 years",
                  detection: "Technology radar gaps, patent analysis misses",
                  recoveryValue: "$50-200M in avoided disruption"
                },
                innovationPipelineLeaks: {
                  impact: "70-90% idea mortality rate",
                  recovery: "3-5x innovation throughput potential"
                },
                marketShiftDetectionDelays: {
                  impact: "12-24 month response lag to disruptions",
                  recovery: "$100-500M in first-mover advantages"
                }
              },
              dataCollection: {
                type: "strategic_intelligence_assessment",
                fields: ["competitive_landscape", "technology_trends", "market_positioning", "innovation_pipeline_analysis"]
              }
            },
            '0.1.3': {
              name: "Digital & Data Asset Leakage Assessment",
              trigger: "Strategic analysis complete",
              activity: "Data monetization potential analysis, digital capability gaps assessment, AI/ML readiness evaluation",
              output: "Digital asset monetization roadmap with identified leakage points and activation strategies",
              dependencies: ['0.1.2'],
              estimatedDuration: "3 weeks",
              risks: ["Data quality issues", "Privacy concerns", "Technical capability gaps"],
              valueLeakageAnalysis: true,
              digitalLeakagePoints: {
                unmonetizedDataAssets: {
                  customerBehaviorData: "10-25% revenue uplift potential",
                  operationalIoTData: "15-30% maintenance cost reduction",
                  supplyChainData: "20-35% logistics optimization"
                },
                aiMlCapabilityGaps: {
                  noProprietaryAlgorithms: "40-60% margin leakage to vendors",
                  weakDataFlywheel: "2-4x slower model evolution",
                  talentConcentrationRisk: "30-50% team productivity impact"
                }
              },
              dataCollection: {
                type: "digital_asset_assessment",
                fields: ["data_monetization_potential", "ai_ml_capability_gaps", "digital_transformation_readiness"]
              }
            }
          }
        },
        0.2: {
          name: "Comprehensive Feasibility & Value Recovery Planning",
          subSteps: {
            '0.2.1': {
              name: "Value Recovery Roadmap Development",
              trigger: "Complete leakage assessment finalized",
              activity: "Phased recovery planning with immediate wins, medium-term initiatives, and long-term transformation",
              output: "Comprehensive value recovery roadmap with $50-200M+ identified potential",
              dependencies: ['0.1.3'],
              estimatedDuration: "4 weeks",
              risks: ["Over-ambitious timelines", "Underestimated implementation complexity", "Resource constraints"],
              valueLeakageAnalysis: true,
              recoveryRoadmap: {
                immediateWins: {
                  licenseOptimization: "15-25% cost reduction",
                  processAutomation: "20-40% efficiency gain",
                  vendorConsolidation: "10-20% procurement savings",
                  timeline: "0-6 months"
                },
                mediumTermInitiatives: {
                  systemRationalization: "25-40% TCO reduction",
                  talentOptimization: "30-50% productivity improvement",
                  dataMonetization: "10-25% new revenue streams",
                  timeline: "6-18 months"
                },
                longTermTransformation: {
                  digitalTransformation: "40-60% operational efficiency",
                  innovationCulture: "2-5x innovation output",
                  ecosystemLeverage: "30-50% market expansion",
                  timeline: "18-36 months"
                }
              },
              dataCollection: {
                type: "recovery_roadmap",
                fields: ["phased_initiatives", "value_targets", "implementation_timeline", "resource_requirements"]
              }
            },
            '0.2.2': {
              name: "Ecosystem & Partnership Value Optimization Planning",
              trigger: "Recovery roadmap drafted",
              activity: "Partnership inefficiencies analysis, platform strategy development, network effects planning",
              output: "Ecosystem optimization strategy with partnership value realization plan",
              dependencies: ['0.2.1'],
              estimatedDuration: "3 weeks",
              risks: ["Partnership resistance", "IP protection issues", "Coordination complexity"],
              valueLeakageAnalysis: true,
              ecosystemLeakagePoints: {
                partnershipInefficiencies: {
                  strategicAlliances: "40-70% partnership value unrealized",
                  supplierInnovation: "25-45% supplier capability untapped",
                  academicResearch: "3-5 year commercialization delay"
                },
                platformOpportunities: {
                  apiEconomy: "$100M-$1B ecosystem value potential",
                  marketplaceCreation: "Complete platform revenue stream",
                  dataNetworkEffects: "Exponential value growth potential"
                }
              },
              dataCollection: {
                type: "ecosystem_optimization",
                fields: ["partnership_analysis", "platform_strategy", "network_effects_potential"]
              }
            },
            '0.2.3': {
              name: "Organizational Learning & Adaptation Capability Assessment",
              trigger: "Ecosystem planning complete",
              activity: "Knowledge management evaluation, adaptation velocity analysis, learning organization readiness",
              output: "Organizational learning enhancement strategy with adaptation acceleration plan",
              dependencies: ['0.2.2'],
              estimatedDuration: "3 weeks",
              risks: ["Cultural resistance", "Knowledge capture challenges", "Adaptation inertia"],
              valueLeakageAnalysis: true,
              organizationalLeakagePoints: {
                knowledgeManagementFailures: {
                  tacitKnowledgeLoss: "20-40% productivity hit with expert departure",
                  lessonsLearnedAmnesia: "40-60% project success rate impact",
                  crossPollinationBarriers: "25-45% innovation velocity reduction"
                },
                adaptationVelocityGaps: {
                  strategicPivotSpeed: "6-18 month lag vs startups",
                  technologyAdoptionRate: "2-4 year enterprise adoption lag",
                  businessModelEvolution: "20-40% revenue stream modernization potential"
                }
              },
              dataCollection: {
                type: "organizational_learning_assessment",
                fields: ["knowledge_management_gaps", "adaptation_velocity", "learning_capability"]
              }
            },
            '0.2.4': {
              name: "Strategic Optionality & Future-Proofing Evaluation",
              trigger: "Organizational assessment complete",
              activity: "Future capability planning, scenario analysis, optionality preservation strategy",
              output: "Strategic optionality framework with future-proofing roadmap",
              dependencies: ['0.2.3'],
              estimatedDuration: "3 weeks",
              risks: ["Strategic myopia", "Underestimation of disruption risks", "Resource allocation errors"],
              valueLeakageAnalysis: true,
              futureLeakagePoints: {
                optionalityValueDestruction: {
                  technologyOptionality: "Complete architectural dependency risk",
                  businessModelOptionality: "80-90% revenue risk concentration",
                  talentOptionality: "Inability to pivot to new opportunities"
                },
                futureProofingFailures: {
                  scenarioPlanningGaps: "50-80% worse disruption response",
                  capabilityBuildingDelays: "3-5 year capability gap",
                  portfolioImbalance: "90%+ resources in core business"
                },
                quantumReadiness: {
                  algorithmAwareness: "2-3 years to irrelevance",
                  talentPipeline: "3-5 year talent gap",
                  useCaseIdentification: "Missed industry transformation"
                }
              },
              dataCollection: {
                type: "strategic_optionality_assessment",
                fields: ["future_capability_gaps", "scenario_planning", "quantum_readiness"]
              }
            }
          }
        }
      }
    },
        1: {
      id: 1,
      name: "Foundation Building & Operational Launch",
      duration: "Months 5-18",
      purpose: "Establish operational credibility and early wins",
      totalValueAtStake: "$20-50M immediate value recovery",
      valueRecoveryFocus: "Immediate Wins (0-6 months) + Quick Operational Fixes",
      phases: {
        1.1: {
          name: "Strategic Blueprinting with Value Leakage Focus",
          subSteps: {
            '1.1.1': {
              name: "Leadership Appointment & Value Recovery Mandate",
              trigger: "Stage 1 funding approved",
              activity: "GCC Head recruitment with value recovery KPIs, leadership team establishment with leakage reduction targets",
              output: "Leadership team with clear value recovery responsibilities and leakage reduction mandates",
              dependencies: ['0.2.4'],
              estimatedDuration: "6 weeks",
              risks: ["Wrong leadership hire", "Long recruitment cycle", "Cultural misfit", "Weak value focus"],
              valueLeakageAnalysis: true,
              targetedLeakageReduction: ["financial.duplicateSystems", "financial.underutilizedLicenses", "process.manualWorkflows"],
              expectedValueRecovery: "$5-15M in first 6 months",
              monitoringMetrics: ["license_cost_reduction", "process_efficiency_gains", "automation_adoption"]
            },
            '1.1.2': {
              name: "Operating Model Design with Leakage Prevention",
              trigger: "Leadership onboarded",
              activity: "Governance framework with value assurance, service delivery model with leakage controls, decision matrix with cost accountability",
              output: "Operating model with built-in leakage prevention and value assurance framework",
              dependencies: ['1.1.1'],
              estimatedDuration: "4 weeks",
              risks: ["Overly complex design", "Cultural misalignment", "Inadequate governance", "Weak leakage controls"],
              valueLeakageAnalysis: true,
              leakagePreventionMechanisms: ["automated_monitoring", "process_governance", "value_assurance"],
              preventionValue: "15-25% leakage reduction through controls"
            },
            '1.1.3': {
              name: "Legal Structure with Compliance Leakage Protection",
              trigger: "Operating model approved",
              activity: "Entity formation with compliance framework, regulatory leakage assessment, legal structure with risk mitigation",
              output: "Registered entity with compliance framework and regulatory leakage protection",
              dependencies: ['1.1.2'],
              estimatedDuration: "3 weeks",
              risks: ["Legal complications", "Regulatory hurdles", "IP protection issues", "Compliance gaps"],
              valueLeakageAnalysis: true,
              complianceLeakageTargets: ["compliance.regulatoryPenalties", "compliance.securityVulnerabilities"],
              riskMitigationValue: "$1-10M potential fine avoidance"
            },
            '1.1.4': {
              name: "Technology Foundation with Digital Leakage Reduction",
              trigger: "Legal structure complete",
              activity: "Digital infrastructure with automation focus, core systems with integration strategy, security framework with vulnerability protection",
              output: "Core systems with leakage prevention capabilities and digital optimization",
              dependencies: ['1.1.3'],
              estimatedDuration: "5 weeks",
              risks: ["Technology integration issues", "Security vulnerabilities", "Vendor lock-in", "Automation gaps"],
              valueLeakageAnalysis: true,
              digitalLeakageReduction: ["technology.legacySystemDrag", "digital.digitalAdoptionGaps", "data.dataQualityIssues"],
              technologyValue: "20-40% efficiency gains through proper foundation"
            }
          }
        },
        1.2: {
          name: "Minimum Viable GCC Launch with Quick Wins",
          subSteps: {
            '1.2.1': {
              name: "Initial Pod Formation with Leakage Expertise",
              trigger: "Infrastructure ready",
              activity: "First 2-3 team formations with leakage reduction skills, skill assessment with cost optimization focus, onboarding with value mindset",
              output: "Functional pods with leakage reduction capabilities and value creation mindset",
              dependencies: ['1.1.4'],
              estimatedDuration: "4 weeks",
              risks: ["Talent acquisition delays", "Wrong skill mix", "Cultural integration issues", "Weak value focus"],
              valueLeakageAnalysis: true,
              podLeakageResponsibilities: {
                "Finance Pod": ["financial.duplicateSystems", "financial.underutilizedLicenses"],
                "Operations Pod": ["process.manualWorkflows", "operational.supplyChainInefficiencies"],
                "IT Pod": ["technology.legacySystemDrag", "data.dataQualityIssues"]
              },
              expectedTeamValue: "$2-5M per pod annual leakage reduction"
            },
            '1.2.2': {
              name: "Process Migration with Knowledge Leakage Prevention",
              trigger: "Teams staffed",
              activity: "Knowledge transfer with retention focus, process documentation with efficiency metrics, training programs with cost awareness",
              output: "Migrated processes with knowledge retention and efficiency optimization",
              dependencies: ['1.2.1'],
              estimatedDuration: "6 weeks",
              risks: ["Knowledge loss", "Process disruption", "Resistance to change", "Efficiency degradation"],
              valueLeakageAnalysis: true,
              knowledgeLeakagePrevention: ["organizationalLearning.tacitKnowledgeLoss", "humanCapital.employeeTurnover"],
              preventionValue: "20-40% productivity preservation"
            },
            '1.2.3': {
              name: "Quick Win Execution & Immediate Value Demonstration",
              trigger: "Processes migrated",
              activity: "90-day value delivery projects targeting highest leakage areas, stakeholder communication with value stories, rapid automation implementation",
              output: "Early success stories with demonstrated leakage reduction and stakeholder confidence",
              dependencies: ['1.2.2'],
              estimatedDuration: "3 months",
              risks: ["Unrealistic expectations", "Scope creep", "Quality compromises", "Value misattribution"],
              valueLeakageAnalysis: true,
              quickWinTargets: {
                "License Optimization": ["financial.underutilizedLicenses", "15-25% cost reduction"],
                "Process Automation": ["process.manualWorkflows", "20-40% efficiency gain"],
                "Vendor Consolidation": ["financial.inefficientProcurement", "10-20% procurement savings"]
              },
              expectedQuickWinValue: "$8-20M annualized"
            },
            '1.2.4': {
              name: "Performance Baseline with Leakage KPIs",
              trigger: "Quick wins delivered",
              activity: "KPI framework with leakage metrics, performance dashboard with value tracking, monitoring framework with early warning indicators",
              output: "Performance dashboard with leakage baselines and value tracking",
              dependencies: ['1.2.3'],
              estimatedDuration: "2 weeks",
              risks: ["Wrong metrics selection", "Data quality issues", "Misaligned incentives", "Measurement gaps"],
              valueLeakageAnalysis: true,
              leakageKPIs: [
                "license_utilization_rate",
                "process_automation_rate", 
                "vendor_consolidation_ratio",
                "knowledge_retention_rate",
                "compliance_gap_closure"
              ],
              monitoringFrequency: "Weekly leakage reviews"
            }
          }
        }
      }
    },

    2: {
      id: 2,
      name: "Scaling & Value Demonstration",
      duration: "Years 2-4",
      purpose: "Prove ROI and build strategic relevance",
      totalValueAtStake: "$50-150M medium-term value recovery",
      valueRecoveryFocus: "Medium-term Initiatives (6-18 months) + Strategic Leakage Reduction",
      phases: {
        2.1: {
          name: "Functional Expansion with Leakage Optimization",
          subSteps: {
            '2.1.1': {
              name: "Portfolio Diversification with Strategic Leakage Focus",
              trigger: "Stage 2 funding approved",
              activity: "New capability assessment with leakage reduction potential, market opportunity analysis with value gaps, expansion planning with ROI focus",
              output: "Expansion roadmap with strategic leakage reduction and capability portfolio",
              dependencies: ['1.3.4'],
              estimatedDuration: "4 weeks",
              risks: ["Over-expansion", "Capability dilution", "Market misread", "Weak value focus"],
              valueLeakageAnalysis: true,
              strategicLeakageTargets: ["strategic.competitiveBlindspots", "innovation.innovationPipelineGaps", "digital.dataMonetizationMissed"],
              expansionValue: "$30-80M strategic leakage reduction"
            },
            '2.1.2': {
              name: "Talent Scaling with Specialized Leakage Expertise",
              trigger: "Expansion plan approved",
              activity: "Specialized recruitment with leakage reduction skills, talent development with value creation focus, retention strategies with performance incentives",
              output: "Expanded team with specialized leakage reduction capabilities and development paths",
              dependencies: ['2.1.1'],
              estimatedDuration: "8 weeks",
              risks: ["Talent market competition", "Cultural dilution", "Skill mismatches", "Retention challenges"],
              valueLeakageAnalysis: true,
              specializedRoles: [
                "Value Leakage Analyst",
                "Process Optimization Specialist", 
                "Data Monetization Lead",
                "Innovation Pipeline Manager"
              ],
              talentValue: "25-45% higher leakage reduction capability"
            },
            '2.1.3': {
              name: "Process Excellence with Advanced Leakage Prevention",
              trigger: "Teams scaled",
              activity: "Advanced automation implementation, process optimization with AI/ML, efficiency programs with predictive analytics",
              output: "Automated processes with advanced leakage prevention and optimization framework",
              dependencies: ['2.1.2'],
              estimatedDuration: "6 weeks",
              risks: ["Automation failures", "Resistance to change", "Technical debt", "Integration complexity"],
              valueLeakageAnalysis: true,
              advancedAutomation: [
                "AI-driven process optimization",
                "Predictive leakage detection",
                "Automated compliance monitoring",
                "Real-time performance analytics"
              ],
              automationValue: "30-50% additional efficiency gains"
            }
          }
        },
        2.2: {
          name: "Value Realization with Leakage Quantification",
          subSteps: {
            '2.2.1': {
              name: "Business Outcome Tracking with Leakage Attribution",
              trigger: "Expanded operations stable",
              activity: "Value realization framework with leakage metrics, benefit tracking with cause attribution, impact measurement with ROI validation",
              output: "Value dashboard with quantified leakage reduction and benefit realization",
              dependencies: ['2.1.4'],
              estimatedDuration: "4 weeks",
              risks: ["Wrong outcome measures", "Attribution challenges", "Measurement gaps", "Data quality issues"],
              valueLeakageAnalysis: true,
              attributionFramework: {
                "Direct Cost Savings": ["financial.*"],
                "Efficiency Gains": ["operational.*", "process.*"],
                "Strategic Value": ["strategic.*", "innovation.*"],
                "Risk Reduction": ["compliance.*", "governance.*"]
              },
              measurementAccuracy: "85-95% leakage attribution"
            },
            '2.2.2': {
              name: "Strategic Leakage Reduction & Business Case Validation",
              trigger: "Positive outcomes demonstrated",
              activity: "Strategic leakage case studies, value storytelling with executive impact, communication with proven ROI",
              output: "Validated business cases with proven strategic leakage reduction and executive confidence",
              dependencies: ['2.2.1'],
              estimatedDuration: "3 weeks",
              risks: ["Poor storytelling", "Lack of compelling evidence", "Communication gaps", "Executive skepticism"],
              valueLeakageAnalysis: true,
              strategicCaseStudies: [
                "Competitive intelligence value demonstration",
                "Innovation pipeline acceleration impact",
                "Digital transformation ROI validation"
              ],
              caseStudyValue: "2-3x additional executive support"
            }
          }
        }
      }
    },

    3: {
      id: 3,
      name: "Strategic Partnership & Innovation",
      duration: "Years 4-6", 
      purpose: "Transition from service provider to innovation partner",
      totalValueAtStake: "$100-300M strategic value creation",
      valueRecoveryFocus: "Long-term Transformation (18-36 months) + Innovation Value",
      phases: {
        3.1: {
          name: "Strategic Integration with Ecosystem Value",
          subSteps: {
            '3.1.1': {
              name: "Business Partnership Model with Value Sharing",
              trigger: "Stage 3 funding approved",
              activity: "Embedded team structure with joint value creation, partnership framework with leakage reduction collaboration, governance with shared incentives",
              output: "Partnership framework with ecosystem value optimization and leakage prevention",
              dependencies: ['2.3.3'],
              estimatedDuration: "5 weeks",
              risks: ["Partnership resistance", "Unclear roles", "Value misalignment", "Governance complexity"],
              valueLeakageAnalysis: true,
              partnershipValueTargets: ["ecosystem.partnershipInefficiencies", "ecosystem.supplierInnovationMissed"],
              partnershipValue: "40-70% better partnership ROI"
            },
            '3.1.2': {
              name: "Innovation Pods with Strategic Leakage Focus",
              trigger: "Partnership model established",
              activity: "Centers of Excellence with leakage innovation focus, specialized team formation with breakthrough potential, R&D with value creation mandate",
              output: "Specialized innovation teams with strategic leakage reduction expertise",
              dependencies: ['3.1.1'],
              estimatedDuration: "6 weeks",
              risks: ["Wrong focus areas", "Talent gaps", "Resource constraints", "Innovation misalignment"],
              valueLeakageAnalysis: true,
              innovationFocusAreas: [
                "AI-driven leakage prediction",
                "Automated value recovery systems",
                "Predictive compliance monitoring",
                "Real-time performance optimization"
              ],
              innovationValue: "2-5x innovation output on leakage reduction"
            }
          }
        },
        3.2: {
          name: "Value Creation Acceleration with Advanced Leakage Prevention",
          subSteps: {
            '3.2.1': {
              name: "Advanced Analytics for Predictive Leakage Detection",
              trigger: "Digital foundation established",
              activity: "AI/ML capability development for leakage prediction, data science platform for value optimization, predictive analytics for risk prevention",
              output: "Advanced analytics with predictive leakage capabilities and optimization insights",
              dependencies: ['3.1.4'],
              estimatedDuration: "10 weeks",
              risks: ["Poor data quality", "Wrong use cases", "Model accuracy issues", "Integration challenges"],
              valueLeakageAnalysis: true,
              predictiveCapabilities: [
                "Leakage risk scoring",
                "Value recovery forecasting",
                "Optimization opportunity identification",
                "Preventive action recommendations"
              ],
              predictiveValue: "40-60% better leakage prevention"
            },
            '3.2.2': {
              name: "IP Creation with Leakage Prevention Innovation",
              trigger: "Innovation pipeline established",
              activity: "Patent filing for leakage prevention methods, IP strategy for value protection, commercialization for leakage solutions",
              output: "Patent portfolio with leakage prevention IP and commercialization value",
              dependencies: ['3.2.4'],
              estimatedDuration: "6 weeks",
              risks: ["Weak IP strategy", "Legal complexities", "Commercialization challenges", "Market adoption"],
              valueLeakageAnalysis: true,
              ipFocusAreas: [
                "Leakage detection algorithms",
                "Value recovery methodologies", 
                "Optimization frameworks",
                "Prevention technologies"
              ],
              ipValue: "$10-50M potential licensing revenue"
            }
          }
        }
      }
    },

    4: {
      id: 4,
      name: "Business Transformation & Market Leadership",
      duration: "Years 6-8",
      purpose: "Redefine business models and create new value streams",
      totalValueAtStake: "$200-500M transformational value",
      valueRecoveryFocus: "Ecosystem Value + Market Leadership Positioning",
      phases: {
        4.1: {
          name: "Business Model Innovation with Leakage Elimination",
          subSteps: {
            '4.1.1': {
              name: "Platform Strategy with Built-in Leakage Prevention",
              trigger: "Stage 4 funding approved",
              activity: "API economy positioning with value optimization, platform architecture with leakage prevention, ecosystem design with value sharing",
              output: "Platform roadmap with inherent leakage prevention and value optimization",
              dependencies: ['3.3.3'],
              estimatedDuration: "8 weeks",
              risks: ["Wrong platform strategy", "Technical limitations", "Adoption challenges", "Value misalignment"],
              valueLeakageAnalysis: true,
              platformLeakagePrevention: ["ecosystem.platformNetworkEffects", "ecosystem.marketplaceCreationMissed"],
              platformValue: "$100M-$1B ecosystem value potential"
            },
            '4.1.2': {
              name: "Data Monetization with Leakage-to-Value Transformation",
              trigger: "Platform strategy defined",
              activity: "New business line creation from leakage insights, data product development with value focus, revenue model with leakage prevention",
              output: "Data products transforming previous leakage points into revenue streams",
              dependencies: ['4.1.1'],
              estimatedDuration: "10 weeks",
              risks: ["Privacy violations", "Poor product-market fit", "Regulatory issues", "Commercialization failure"],
              valueLeakageAnalysis: true,
              monetizationTargets: ["digital.dataMonetizationMissed", "strategic.innovationPipelineGaps"],
              monetizationValue: "15-35% new revenue streams"
            }
          }
        },
        4.2: {
          name: "Ecosystem Orchestration with Value Optimization",
          subSteps: {
            '4.2.1': {
              name: "Open Innovation with Leakage Solution Sharing",
              trigger: "Venture portfolio established",
              activity: "Co-creation ecosystem for leakage solutions, innovation platform with value sharing, community engagement with best practices",
              output: "Innovation platform transforming leakage challenges into collaborative solutions",
              dependencies: ['4.1.5'],
              estimatedDuration: "12 weeks",
              risks: ["Platform adoption issues", "Community management challenges", "IP leakage", "Quality control"],
              valueLeakageAnalysis: true,
              openInnovationValue: ["ecosystem.openInnovationPlatforms", "organizationalLearning.crossPollinationBarriers"],
              collaborationValue: "25-45% faster leakage solution development"
            },
            '4.2.2': {
              name: "Industry Standards Leadership with Leakage Prevention",
              trigger: "Platform operational",
              activity: "Consortium formation for leakage standards, standards development with best practices, industry leadership with value focus",
              output: "Industry leadership position with leakage prevention standards and influence",
              dependencies: ['4.2.1'],
              estimatedDuration: "10 weeks",
              risks: ["Standards rejection", "Competitive resistance", "Consensus challenges", "Adoption barriers"],
              valueLeakageAnalysis: true,
              standardsLeadership: ["ecosystem.industryStandardsLeadership", "brand.industryInfluenceReduction"],
              standardsValue: "2-3x market influence through leadership"
            }
          }
        }
      }
    },

    5: {
      id: 5,
      name: "Autonomous Enterprise & Predictive Intelligence",
      duration: "Years 8-12",
      purpose: "Achieve self-sustaining innovation and market creation",
      totalValueAtStake: "$500M-$1B+ autonomous value creation",
      valueRecoveryFocus: "Predictive Prevention + Autonomous Optimization",
      phases: {
        5.1: {
          name: "AI-First Transformation with Predictive Leakage Prevention",
          subSteps: {
            '5.1.1': {
              name: "Sovereign AI for Autonomous Leakage Detection",
              trigger: "Stage 5 strategy approved",
              activity: "Proprietary AI model development for leakage prediction, custom algorithm creation for value optimization, autonomous systems for prevention",
              output: "Custom AI capabilities with autonomous leakage prevention and value optimization",
              dependencies: ['4.3.3'],
              estimatedDuration: "20 weeks",
              risks: ["AI ethics violations", "Technical debt accumulation", "Talent gaps", "System dependencies"],
              valueLeakageAnalysis: true,
              autonomousCapabilities: [
                "Real-time leakage detection",
                "Predictive value optimization",
                "Autonomous prevention actions",
                "Self-learning improvement"
              ],
              autonomousValue: "80-95% leakage prevention automation"
            },
            '5.1.2': {
              name: "Predictive Enterprise with Foresight Capabilities",
              trigger: "AI capabilities established",
              activity: "Real-time foresight systems for emerging leakage risks, predictive analytics for future value gaps, anticipation capabilities for market shifts",
              output: "Prediction engines with market anticipation and leakage prevention capabilities",
              dependencies: ['5.1.1'],
              estimatedDuration: "12 weeks",
              risks: ["Prediction inaccuracies", "Over-reliance on AI", "Data quality issues", "False positives"],
              valueLeakageAnalysis: true,
              predictiveValue: ["strategicOptionality.existentialRiskExposure", "strategic.competitiveBlindspots"],
              foresightValue: "60-80% better disruption response"
            }
          }
        },
        5.2: {
          name: "Self-Evolving Organization with Continuous Leakage Prevention",
          subSteps: {
            '5.2.1': {
              name: "Dynamic Governance with Automated Leakage Controls",
              trigger: "Culture systems operational",
              activity: "Automated compliance systems with real-time monitoring, self-governing frameworks with adaptive controls, regulatory adaptation with predictive compliance",
              output: "Self-governing systems with automated leakage controls and regulatory adaptation",
              dependencies: ['5.2.1'],
              estimatedDuration: "12 weeks",
              risks: ["Governance failures", "Regulatory non-compliance", "Oversight gaps", "Control weaknesses"],
              valueLeakageAnalysis: true,
              automatedGovernance: ["governance.decisionDelays", "compliance.regulatoryPenalties"],
              governanceValue: "90-95% compliance automation"
            }
          }
        }
      }
    },

    6: {
      id: 6,
      name: "Corporate Singularity & Transcendent Impact",
      duration: "Years 12+",
      purpose: "Ensure perpetual relevance and industry transformation",
      totalValueAtStake: "Perpetual value creation and industry leadership",
      valueRecoveryFocus: "Industry Transformation + Legacy Value Creation",
      phases: {
        6.1: {
          name: "Transcendent Impact with Industry-Wide Leakage Elimination",
          subSteps: {
            '6.1.1': {
              name: "Industry Redefinition with Leakage-Free Standards",
              trigger: "Evolution framework established",
              activity: "Category creation with built-in leakage prevention, market transformation with value optimization, industry standards with best practices",
              output: "New industry categories with leakage-free operations and market leadership",
              dependencies: ['5.3.3'],
              estimatedDuration: "16 weeks",
              risks: ["Industry resistance", "Regulatory barriers", "Adoption challenges", "Standardization failures"],
              valueLeakageAnalysis: true,
              industryTransformation: ["ecosystem.industryStandardsLeadership", "strategicOptionality.existentialRiskExposure"],
              transformationValue: "Industry-wide leakage reduction leadership"
            },
            '6.1.2': {
              name: "Knowledge Commons with Leakage Prevention Legacy",
              trigger: "Moonshots operational",
              activity: "Open source leadership for leakage solutions, knowledge sharing platforms for best practices, collaborative ecosystem for value creation",
              output: "Open innovation commons transforming leakage prevention into industry standard",
              dependencies: ['6.1.3'],
              estimatedDuration: "8 weeks",
              risks: ["IP leakage", "Free rider problem", "Quality control issues", "Community management"],
              valueLeakageAnalysis: true,
              knowledgeCommons: ["organizationalLearning.lessonsLearnedAmnesia", "ecosystem.openInnovationPlatforms"],
              commonsValue: "Industry-wide value creation acceleration"
            }
          }
        },
        6.2: {
          name: "Perpetual Evolution with Continuous Leakage Innovation",
          subSteps: {
            '6.2.1': {
              name: "Autonomous Strategy with AI-Led Leakage Prevention",
              trigger: "Convergence established",
              activity: "AI-led direction setting for value optimization, autonomous decision systems for leakage prevention, continuous optimization for maximum value",
              output: "Self-directing strategy with continuous leakage prevention and value optimization",
              dependencies: ['6.2.3'],
              estimatedDuration: "12 weeks",
              risks: ["Loss of human agency", "Strategic misalignment with values", "Ethical dilemmas", "Over-optimization"],
              valueLeakageAnalysis: true,
              autonomousStrategy: ["strategicOptionality.scenarioPlanningGaps", "governance.decisionDelays"],
              autonomousValue: "Perpetual relevance and value optimization"
            }
          }
        }
      }
    }
  }
};

export const COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK = {
  categories: {
    financial: {
      id: 'financial',
      name: 'Financial Value Leakage',
      impact: 'VERY HIGH',
      recoverability: 'HIGH',
      description: 'Direct monetary losses through inefficiencies and missed opportunities',
      weight: 25,
      totalAddressableValue: "15-35% operational cost savings",
      subTypes: {
        duplicateSystems: {
          name: 'Duplicate Systems & Licenses',
          impact: '$500K - $5M',
          detection: 'System audit, license reconciliation, usage analysis',
          recovery: '15-25% cost reduction through consolidation',
          priority: 'IMMEDIATE',
          businessCase: 'Consolidate redundant systems and optimize license utilization',
          riskMitigation: 'Centralized procurement, usage monitoring, regular audits',
          painPointWeight: 8,
          symptoms: ['Multiple software doing same function', 'Overlapping license costs', 'Redundant maintenance contracts'],
          quantification: "(Redundant licenses + Maintenance) * Systems count",
          typicalRange: "$500K - $5M"
        },
        underutilizedLicenses: {
          name: 'Underutilized Software Licenses',
          impact: '$250K - $2M',
          detection: 'Usage analytics, ROI per license, feature utilization',
          recovery: '20-35% optimization through rightsizing',
          priority: 'IMMEDIATE',
          businessCase: 'Implement license management and optimize subscription levels',
          riskMitigation: 'Regular usage reviews, flexible licensing models',
          painPointWeight: 7,
          symptoms: ['Low feature adoption rates', 'Unused seat licenses', 'Premium features not utilized'],
          quantification: "(Licenses unused * cost) + (Premium features unused * value)",
          typicalRange: "$250K - $2M"
        },
        redundantProcesses: {
          name: 'Manual Data Entry & Redundant Processes',
          impact: '$1M - $8M',
          detection: 'Process mining, FTE time tracking, automation opportunity assessment',
          recovery: '20-40% efficiency gain through automation',
          priority: 'IMMEDIATE',
          businessCase: 'Implement workflow automation and process optimization',
          riskMitigation: 'Process standardization, automation tools, continuous improvement',
          painPointWeight: 8,
          symptoms: ['Manual data entry across systems', 'Repetitive tasks', 'High error rates'],
          quantification: "(FTE hours wasted * loaded cost) + Opportunity cost",
          typicalRange: "$1M - $8M"
        },
        inefficientProcurement: {
          name: 'Decentralized Vendor Management',
          impact: '$750K - $4M',
          detection: 'Spend analysis, contract consolidation, vendor performance tracking',
          recovery: '10-20% procurement savings through consolidation',
          priority: 'IMMEDIATE',
          businessCase: 'Centralize procurement and implement vendor management system',
          riskMitigation: 'Centralized procurement, vendor performance management',
          painPointWeight: 7,
          symptoms: ['Multiple vendors for same service', 'Inconsistent pricing', 'Poor contract terms'],
          quantification: "Spend fragmentation * consolidation savings potential",
          typicalRange: "$750K - $4M"
        },
        pricingInefficiencies: {
          name: 'Non-optimized Pricing Models',
          impact: '3-15% revenue loss',
          detection: 'Price benchmarking, elasticity analysis, competitive intelligence',
          recovery: '5-15% revenue improvement through pricing optimization',
          priority: 'HIGH',
          businessCase: 'Implement dynamic pricing and value-based pricing models',
          riskMitigation: 'Regular price reviews, competitive monitoring, customer value analysis',
          painPointWeight: 8,
          symptoms: ['Static pricing', 'Poor competitive intelligence', 'Price-value misalignment'],
          quantification: "(Theoretical max revenue - Actual revenue) * Margin",
          typicalRange: "3-15% of revenue"
        }
      }
    },

    strategic: {
      id: 'strategic',
      name: 'Strategic Intelligence Leakage',
      impact: 'VERY HIGH',
      recoverability: 'MEDIUM',
      description: 'Loss of competitive advantage and market positioning',
      weight: 20,
      totalAddressableValue: "$200-500M strategic value at risk",
      subTypes: {
        competitiveBlindspots: {
          name: 'Competitive Intelligence Gaps',
          impact: '40-60% market share loss risk',
          detection: 'Technology radar gaps, patent analysis, market monitoring',
          recovery: '$50-200M disruption avoidance through proactive intelligence',
          priority: 'HIGH',
          businessCase: 'Establish competitive intelligence function and market monitoring',
          riskMitigation: 'Regular competitive analysis, technology scouting, signal detection',
          painPointWeight: 9,
          symptoms: ['Surprised by competitor moves', 'Late to market trends', 'No systematic competitor tracking'],
          quantification: "Market share loss * margin impact",
          typicalRange: "$50-200M avoided disruption"
        },
        slowTimeToMarket: {
          name: 'Slow Innovation Commercialization',
          impact: '20-60% market share loss',
          detection: 'Time-to-market benchmarks, competitor tracking, pipeline velocity analysis',
          recovery: '40-60% faster innovation through streamlined processes',
          priority: 'HIGH',
          businessCase: 'Implement agile innovation processes and reduce bureaucracy',
          riskMitigation: 'Stage-gate processes, cross-functional teams, rapid prototyping',
          painPointWeight: 8,
          symptoms: ['Long development cycles', 'Missed market windows', 'Bureaucratic approval processes'],
          quantification: "Time delay * market window value",
          typicalRange: "20-60% market share impact"
        },
        innovationPipelineGaps: {
          name: 'Missed Market Opportunities',
          impact: '$10M - $100M+ potential revenue',
          detection: 'Innovation portfolio analysis, patent gap assessment, market adjacency analysis',
          recovery: '$50-200M revenue potential through pipeline optimization',
          priority: 'HIGH',
          businessCase: 'Create innovation pipeline management and opportunity identification',
          riskMitigation: 'Innovation portfolio management, market sensing, venture scouting',
          painPointWeight: 8,
          symptoms: ['Weak innovation pipeline', 'Missed adjacent markets', 'No disruptive innovation'],
          quantification: "Opportunity pipeline value * success probability",
          typicalRange: "$10M - $100M+"
        }
      }
    },

    digital: {
      id: 'digital',
      name: 'Digital Transformation Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss through poor digital capability and transformation delays',
      weight: 18,
      totalAddressableValue: "40-60% operational efficiency potential",
      subTypes: {
        legacySystemDrag: {
          name: 'Legacy System Technical Debt',
          impact: '25-40% IT budget wasted',
          detection: 'Technical debt quantification, modernization ROI analysis, system complexity assessment',
          recovery: '30-50% efficiency through modernization',
          priority: 'MEDIUM',
          businessCase: 'Implement technology modernization and cloud migration',
          riskMitigation: 'Modernization roadmap, cloud strategy, legacy retirement',
          painPointWeight: 8,
          symptoms: ['High maintenance costs', 'Integration challenges', 'Security vulnerabilities'],
          quantification: "Maintenance cost + Opportunity cost + Risk cost",
          typicalRange: "25-40% IT budget"
        },
        digitalAdoptionGaps: {
          name: 'Underutilized Digital Tools',
          impact: '40-70% capability unrealized',
          detection: 'Adoption metrics, feature usage analysis, user experience assessment',
          recovery: '40-70% better utilization through improved adoption',
          priority: 'MEDIUM',
          businessCase: 'Implement change management and user experience optimization',
          riskMitigation: 'User training, UX improvement, change management',
          painPointWeight: 7,
          symptoms: ['Low feature adoption', 'Poor user experience', 'Resistance to digital tools'],
          quantification: "Tool capability value * adoption rate gap",
          typicalRange: "40-70% capability loss"
        },
        dataMonetizationMissed: {
          name: 'Unused Data Assets',
          impact: '15-35% revenue potential',
          detection: 'Data asset valuation, external benchmarking, monetization opportunity analysis',
          recovery: '10-25% new revenue streams through data monetization',
          priority: 'HIGH',
          businessCase: 'Develop data monetization strategy and implementation plan',
          riskMitigation: 'Data governance, privacy compliance, value realization tracking',
          painPointWeight: 8,
          symptoms: ['Unanalyzed customer data', 'Untapped operational data', 'No data products'],
          quantification: "Data asset value * monetization rate",
          typicalRange: "15-35% revenue potential"
        }
      }
    },
     //Mandar
     operational: {
      id: 'operational',
      name: 'Operational Value Leakage',
      impact: 'HIGH',
      recoverability: 'HIGH',
      description: 'Value loss through process inefficiencies and operational gaps',
      weight: 22,
      totalAddressableValue: "25-50% process efficiency improvement",
      subTypes: {
        supplyChainInefficiencies: {
          name: 'Inventory Mismanagement & Supply Chain Gaps',
          impact: '15-25% carrying costs',
          detection: 'Inventory turnover analysis, stockout costs, demand forecasting accuracy',
          recovery: '20-35% optimization through better planning',
          priority: 'IMMEDIATE',
          businessCase: 'Implement inventory optimization and demand forecasting systems',
          riskMitigation: 'Inventory management systems, supplier collaboration, buffer optimization',
          painPointWeight: 7,
          symptoms: ['Excess inventory', 'Stockouts', 'Poor demand forecasting', 'Bullwhip effect'],
          quantification: "(Carrying costs + Stockout costs) * inefficiency factor",
          typicalRange: "15-25% carrying costs"
        },
        manufacturingInefficiencies: {
          name: 'Production Line & Manufacturing Gaps',
          impact: '8-20% capacity loss',
          detection: 'OEE monitoring, bottleneck analysis, equipment downtime tracking',
          recovery: '15-30% capacity improvement through optimization',
          priority: 'HIGH',
          businessCase: 'Implement manufacturing execution systems and bottleneck elimination',
          riskMitigation: 'Preventive maintenance, line balancing, quality control',
          painPointWeight: 7,
          symptoms: ['Equipment downtime', 'Changeover delays', 'Quality variations', 'Capacity constraints'],
          quantification: "Theoretical capacity - Actual output * margin impact",
          typicalRange: "8-20% capacity loss"
        },
        serviceDeliveryGaps: {
          name: 'Resource Utilization & Service Delivery Inefficiencies',
          impact: '20-40% utilization loss',
          detection: 'Utilization tracking, project profitability analysis, skill matching assessment',
          recovery: '25-45% utilization improvement through optimization',
          priority: 'HIGH',
          businessCase: 'Implement resource management and capacity planning systems',
          riskMitigation: 'Skills inventory, capacity planning, performance monitoring',
          painPointWeight: 8,
          symptoms: ['Poor scheduling', 'Skill mismatches', 'Low billable utilization', 'Project overruns'],
          quantification: "(Theoretical utilization - Actual utilization) * loaded cost",
          typicalRange: "20-40% utilization loss"
        },
        manualWorkflows: {
          name: 'Manual Process Inefficiencies & Automation Gaps',
          impact: '25-50% time waste',
          detection: 'Process mining, time tracking, automation opportunity assessment',
          recovery: '20-40% efficiency gain through automation',
          priority: 'IMMEDIATE',
          businessCase: 'Implement workflow automation and process optimization',
          riskMitigation: 'Process standardization, automation tools, continuous improvement',
          painPointWeight: 8,
          symptoms: ['Manual data entry', 'Repetitive tasks', 'High error rates', 'Process variations'],
          quantification: "(FTE hours wasted * loaded cost) + Error correction costs",
          typicalRange: "25-50% time waste"
        }
      }
    },

    humanCapital: {
      id: 'humanCapital',
      name: 'Human Capital Value Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss through poor talent utilization and development',
      weight: 20,
      totalAddressableValue: "30-50% productivity improvement potential",
      subTypes: {
        employeeTurnover: {
          name: 'Knowledge Loss & Replacement Costs',
          impact: '50-200% of annual salary',
          detection: 'Turnover cost analysis, exit interview patterns, retention metrics',
          recovery: '30-50% turnover reduction through better engagement',
          priority: 'HIGH',
          businessCase: 'Create retention programs and career development paths',
          riskMitigation: 'Employee engagement, career development, competitive compensation',
          painPointWeight: 8,
          symptoms: ['High turnover rates', 'Knowledge loss', 'Recruitment costs', 'Onboarding time'],
          quantification: "(Recruitment + Training + Productivity loss) * turnover rate",
          typicalRange: "50-200% of annual salary"
        },
        skillGaps: {
          name: 'Unfilled Capability Needs & Skill Deficiencies',
          impact: '20-40% productivity loss',
          detection: 'Skills gap analysis, performance metrics, capability assessment',
          recovery: '25-45% productivity improvement through skill development',
          priority: 'MEDIUM',
          businessCase: 'Implement skills development and training programs',
          riskMitigation: 'Training programs, skills assessment, talent development',
          painPointWeight: 7,
          symptoms: ['Unfilled positions', 'Performance issues', 'Training gaps', 'Technology skill shortages'],
          quantification: "Productivity loss * average salary * affected workforce",
          typicalRange: "20-40% productivity loss"
        },
        talentUnderutilization: {
          name: 'Unused Employee Capabilities & Potential',
          impact: '30-50% productivity potential unrealized',
          detection: 'Skills inventory vs utilization analysis, employee survey data',
          recovery: '30-50% better utilization through capability matching',
          priority: 'MEDIUM',
          businessCase: 'Create internal mobility and talent optimization programs',
          riskMitigation: 'Skills inventory, internal mobility, talent development',
          painPointWeight: 7,
          symptoms: ['Rigid roles', 'Lack of internal mobility', 'Untapped skills', 'Employee disengagement'],
          quantification: "Skill potential value * utilization gap",
          typicalRange: "30-50% productivity potential"
        }
      }
    },

    customer: {
      id: 'customer',
      name: 'Customer Experience Leakage',
      impact: 'HIGH',
      recoverability: 'HIGH',
      description: 'Value loss through poor customer experiences and retention issues',
      weight: 18,
      totalAddressableValue: "5-20% revenue growth through leakage plugging",
      subTypes: {
        customerChurn: {
          name: 'Preventable Customer Attrition',
          impact: '10-30% revenue at risk',
          detection: 'Churn analysis, customer lifetime value tracking, NPS correlation',
          recovery: '15-25% retention improvement through better engagement',
          priority: 'HIGH',
          businessCase: 'Implement customer success programs and proactive retention',
          riskMitigation: 'Customer feedback systems, loyalty programs, relationship management',
          painPointWeight: 9,
          symptoms: ['High customer turnover', 'Low renewal rates', 'Poor customer satisfaction', 'Negative feedback'],
          quantification: "Customer lifetime value * churn rate * margin",
          typicalRange: "10-30% revenue at risk"
        },
        serviceGaps: {
          name: 'Inconsistent Service Quality & Experience',
          impact: '20-35% customer satisfaction impact',
          detection: 'CSAT scores, service level analysis, quality metrics',
          recovery: '20-40% satisfaction improvement through quality programs',
          priority: 'HIGH',
          businessCase: 'Implement service quality standards and customer experience management',
          riskMitigation: 'Quality standards, training, monitoring, continuous improvement',
          painPointWeight: 8,
          symptoms: ['Process variations', 'Training gaps', 'Inconsistent delivery', 'Customer complaints'],
          quantification: "Satisfaction impact * customer value * retention risk",
          typicalRange: "20-35% customer satisfaction impact"
        },
        onboardingFriction: {
          name: 'Slow Customer Activation & Onboarding Delays',
          impact: '30-60% early churn risk',
          detection: 'Time-to-value metrics, onboarding drop-off analysis, activation rates',
          recovery: '25-50% faster activation through streamlined onboarding',
          priority: 'MEDIUM',
          businessCase: 'Create streamlined onboarding and customer activation processes',
          riskMitigation: 'Onboarding optimization, customer education, success tracking',
          painPointWeight: 7,
          symptoms: ['Complex setup', 'Poor guidance', 'Slow activation', 'Early drop-offs'],
          quantification: "Activation delay * churn risk * customer value",
          typicalRange: "30-60% early churn risk"
        },
        upsellMissed: {
          name: 'Untapped Account Potential & Cross-sell Opportunities',
          impact: '15-40% revenue opportunity',
          detection: 'Customer lifetime value analysis, product penetration, account growth potential',
          recovery: '20-35% revenue growth through better account management',
          priority: 'HIGH',
          businessCase: 'Implement account growth strategies and customer success programs',
          riskMitigation: 'Account planning, customer success, product education',
          painPointWeight: 8,
          symptoms: ['Siloed customer data', 'Poor segmentation', 'Limited account growth', 'Missed opportunities'],
          quantification: "Account potential value * penetration gap",
          typicalRange: "15-40% revenue opportunity"
        }
      }
    },

    compliance: {
      id: 'compliance',
      name: 'Compliance & Regulatory Leakage',
      impact: 'MEDIUM',
      recoverability: 'HIGH',
      description: 'Value loss through regulatory penalties and compliance failures',
      weight: 16,
      totalAddressableValue: "$1M - $100M+ fines avoidance",
      subTypes: {
        regulatoryPenalties: {
          name: 'Regulatory Compliance Gaps & Control Failures',
          impact: '$1M - $100M+ potential fines',
          detection: 'Compliance audit findings, regulatory change tracking, control assessments',
          recovery: 'Avoidance of penalties through proactive compliance',
          priority: 'IMMEDIATE',
          businessCase: 'Establish compliance framework and monitoring systems',
          riskMitigation: 'Regular audits, compliance training, monitoring tools',
          painPointWeight: 9,
          symptoms: ['Frequent compliance violations', 'Late regulatory adaptation', 'Inadequate documentation', 'Control gaps'],
          quantification: "Fine probability * fine amount + remediation costs",
          typicalRange: "$1M - $100M+ potential fines"
        },
        securityVulnerabilities: {
          name: 'Data Breaches & System Security Gaps',
          impact: '$4M average breach cost',
          detection: 'Security assessment scores, incident frequency, vulnerability scans',
          recovery: 'Breach cost avoidance through security improvements',
          priority: 'IMMEDIATE',
          businessCase: 'Implement comprehensive security framework and incident response',
          riskMitigation: 'Security controls, employee training, incident response planning',
          painPointWeight: 9,
          symptoms: ['Outdated security', 'Human error', 'System vulnerabilities', 'Poor access controls'],
          quantification: "Breach probability * average breach cost + reputation impact",
          typicalRange: "$4M average breach cost"
        },
        businessContinuityGaps: {
          name: 'Downtime Costs & Recovery Failures',
          impact: '$5K - $100K+ per hour',
          detection: 'RTO/RPO compliance, recovery test results, single point analysis',
          recovery: 'Downtime cost avoidance through better continuity planning',
          priority: 'HIGH',
          businessCase: 'Create business continuity and disaster recovery capabilities',
          riskMitigation: 'Redundancy, backup systems, recovery testing, failover planning',
          painPointWeight: 8,
          symptoms: ['Single points of failure', 'Poor DR planning', 'Extended downtime', 'Recovery failures'],
          quantification: "Downtime probability * cost per hour * duration",
          typicalRange: "$5K - $100K+ per hour"
        }
      }
    },

    innovation: {
      id: 'innovation',
      name: 'Innovation Pipeline Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss through inefficient innovation processes and missed opportunities',
      weight: 19,
      totalAddressableValue: "2-5x innovation output potential",
      subTypes: {
        innovationFriction: {
          name: 'Ideation-to-Commercialization Bureaucracy',
          impact: '70-90% idea mortality rate',
          detection: 'Innovation pipeline analysis, stage-gate metrics, time-to-commercialization',
          recovery: '3-5x innovation throughput through streamlined processes',
          priority: 'HIGH',
          businessCase: 'Implement agile innovation processes and reduce bureaucracy',
          riskMitigation: 'Stage-gate optimization, cross-functional teams, rapid experimentation',
          painPointWeight: 8,
          symptoms: ['Bureaucratic gates', 'Risk-averse culture', 'Long approval cycles', 'Idea stagnation'],
          quantification: "Idea potential value * mortality rate * time delay",
          typicalRange: "70-90% idea mortality rate"
        },
        adjacencyBlindness: {
          name: 'Missed Market Adjacency Opportunities',
          impact: '$500M-$2B missed market expansion',
          detection: 'Market adjacency analysis, portfolio gap assessment, competitive positioning',
          recovery: '25-40% revenue growth potential through market expansion',
          priority: 'HIGH',
          businessCase: 'Create market adjacency strategy and opportunity identification',
          riskMitigation: 'Market research, portfolio planning, venture scouting',
          painPointWeight: 8,
          symptoms: ['Siloed thinking', 'Narrow market definition', 'Missed adjacencies', 'Limited portfolio'],
          quantification: "Adjacent market size * capture probability * margin",
          typicalRange: "$500M-$2B missed market expansion"
        },
        ecosystemDisconnect: {
          name: 'Startup & Emerging Technology Gaps',
          impact: '2-3 year technology adoption lag',
          detection: 'Technology adoption benchmarks, startup partnership analysis, innovation ecosystem mapping',
          recovery: '40-60% faster emerging tech adoption through ecosystem engagement',
          priority: 'MEDIUM',
          businessCase: 'Build startup partnerships and emerging technology scouting',
          riskMitigation: 'Venture relationships, technology scouting, partnership development',
          painPointWeight: 7,
          symptoms: ['Not-invented-here syndrome', 'Poor venture scouting', 'Technology lag', 'Innovation isolation'],
          quantification: "Technology advantage value * adoption delay cost",
          typicalRange: "2-3 year technology adoption lag"
        }
      }
    },

    talent: {
      id: 'talent',
      name: 'Talent Management Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss through poor talent utilization and development',
      weight: 17,
      totalAddressableValue: "30-50% productivity improvement",
      subTypes: {
        leadershipGaps: {
          name: 'Leadership Pipeline Deficiencies & Succession Risks',
          impact: '25-50% team performance variation',
          detection: 'Leadership assessment, succession planning gaps, team performance analysis',
          recovery: '30-60% leadership effectiveness through development',
          priority: 'HIGH',
          businessCase: 'Create leadership development and succession planning programs',
          riskMitigation: 'Leadership assessment, development programs, succession planning',
          painPointWeight: 8,
          symptoms: ['Leadership vacancies', 'Poor team performance', 'Lack of succession readiness', 'Management gaps'],
          quantification: "Team performance impact * leader count * salary multiplier",
          typicalRange: "25-50% team performance variation"
        },
        collaborationInefficiencies: {
          name: 'Meeting Overhead & Communication Gaps',
          impact: '15-30% productive time loss',
          detection: 'Meeting effectiveness surveys, collaboration pattern analysis, communication audits',
          recovery: '20-40% time recovery through collaboration optimization',
          priority: 'MEDIUM',
          businessCase: 'Implement collaboration tools and meeting efficiency programs',
          riskMitigation: 'Meeting standards, collaboration tools, communication training',
          painPointWeight: 7,
          symptoms: ['Excessive meetings', 'Poor communication', 'Organizational complexity', 'Inefficient tools'],
          quantification: "Time wasted * employee count * loaded cost",
          typicalRange: "15-30% productive time loss"
        },
        strategicMisalignment: {
          name: 'Divergent Goals & Execution Friction',
          impact: '20-40% execution efficiency loss',
          detection: 'Strategy deployment mapping, goal alignment surveys, performance metric analysis',
          recovery: '25-45% execution improvement through better alignment',
          priority: 'HIGH',
          businessCase: 'Implement strategic alignment and goal cascade processes',
          riskMitigation: 'Strategy communication, goal cascade, performance management',
          painPointWeight: 8,
          symptoms: ['Poor communication', 'Conflicting KPIs', 'Departmental silos', 'Execution friction'],
          quantification: "Efficiency loss * operational costs * strategic impact",
          typicalRange: "20-40% execution efficiency loss"
        }
      }
    },

    technology: {
      id: 'technology',
      name: 'Technology Infrastructure Leakage',
      impact: 'HIGH',
      recoverability: 'HIGH',
      description: 'Value loss through outdated technology and infrastructure gaps',
      weight: 16,
      totalAddressableValue: "25-40% IT budget optimization",
      subTypes: {
        legacySystemDrag: {
          name: 'Legacy System Technical Debt & Modernization Delays',
          impact: '25-40% IT budget wasted',
          detection: 'Technical debt quantification, modernization ROI analysis, system complexity assessment',
          recovery: '30-50% efficiency through modernization',
          priority: 'MEDIUM',
          businessCase: 'Implement technology modernization and cloud migration',
          riskMitigation: 'Modernization roadmap, cloud strategy, legacy retirement',
          painPointWeight: 8,
          symptoms: ['High maintenance costs', 'Integration challenges', 'Security vulnerabilities', 'Skill shortages'],
          quantification: "Maintenance cost + Opportunity cost + Risk cost",
          typicalRange: "25-40% IT budget"
        },
        dataSilos: {
          name: 'Inaccessible Data Assets & Integration Gaps',
          impact: '15-30% decision quality loss',
          detection: 'Data flow mapping, access pattern analysis, integration complexity scoring',
          recovery: '20-40% better decisions through data accessibility',
          priority: 'MEDIUM',
          businessCase: 'Implement data integration and accessibility solutions',
          riskMitigation: 'Data governance, integration platforms, access management',
          painPointWeight: 7,
          symptoms: ['Departmental ownership', 'Integration gaps', 'Data duplication', 'Access limitations'],
          quantification: "Decision quality impact * business value * frequency",
          typicalRange: "15-30% decision quality loss"
        },
        systemIntegrationGaps: {
          name: 'Manual Data Transfers & Process Disconnects',
          impact: '20-40% process time increase',
          detection: 'Integration complexity scoring, manual touchpoint count, process delay analysis',
          recovery: '25-45% efficiency through integration automation',
          priority: 'HIGH',
          businessCase: 'Implement API strategy and integration automation',
          riskMitigation: 'API management, integration platforms, process automation',
          painPointWeight: 8,
          symptoms: ['Point solutions', 'Lack of API strategy', 'Manual transfers', 'Process disconnects'],
          quantification: "Manual effort time * frequency * loaded cost",
          typicalRange: "20-40% process time increase"
        }
      }
    },

    process: {
      id: 'process',
      name: 'Process Efficiency Leakage',
      impact: 'HIGH',
      recoverability: 'HIGH',
      description: 'Value loss through inefficient processes and workflows',
      weight: 15,
      totalAddressableValue: "20-40% process efficiency improvement",
      subTypes: {
        manualWorkflows: {
          name: 'Manual Process Inefficiencies & Automation Opportunities',
          impact: '25-50% time waste',
          detection: 'Process mining, time tracking, efficiency analysis',
          recovery: '20-40% efficiency gain through automation',
          priority: 'IMMEDIATE',
          businessCase: 'Implement workflow automation and process optimization',
          riskMitigation: 'Process standardization, automation tools, continuous improvement',
          painPointWeight: 8,
          symptoms: ['Manual data entry', 'Repetitive tasks', 'High error rates', 'Process variations'],
          quantification: "(FTE hours wasted * loaded cost) + Error correction costs",
          typicalRange: "25-50% time waste"
        },
        salesCycleDelays: {
          name: 'Extended Sales Cycles & Conversion Friction',
          impact: '5-20% conversion loss',
          detection: 'Sales process mapping, cycle time analysis, conversion rate tracking',
          recovery: '15-30% conversion improvement through process optimization',
          priority: 'HIGH',
          businessCase: 'Implement sales process optimization and automation',
          riskMitigation: 'Sales process redesign, automation, performance coaching',
          painPointWeight: 8,
          symptoms: ['Complex approval processes', 'Poor lead routing', 'Administrative burden', 'Decision delays'],
          quantification: "Cycle time extension * deal value * conversion impact",
          typicalRange: "5-20% conversion loss"
        }
      }
    },

    data: {
      id: 'data',
      name: 'Data Asset Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss through poor data utilization and management',
      weight: 14,
      totalAddressableValue: "15-35% revenue potential through data monetization",
      subTypes: {
        dataQualityIssues: {
          name: 'Poor Data Quality & Governance Gaps',
          impact: '15-30% decision quality loss',
          detection: 'Data quality assessment, governance maturity evaluation, error rate analysis',
          recovery: '25-40% better decisions through data quality',
          priority: 'MEDIUM',
          businessCase: 'Implement data governance and quality management',
          riskMitigation: 'Data quality framework, governance processes, master data management',
          painPointWeight: 7,
          symptoms: ['Inconsistent data', 'Poor reporting quality', 'Decision errors', 'Duplicate records'],
          quantification: "Decision error cost * frequency + cleanup costs",
          typicalRange: "15-30% decision quality loss"
        },
        unmonetizedDataAssets: {
          name: 'Untapped Data Monetization Opportunities',
          impact: '15-35% revenue potential',
          detection: 'Data asset valuation, external benchmarking, monetization opportunity analysis',
          recovery: '10-25% new revenue streams through data monetization',
          priority: 'HIGH',
          businessCase: 'Develop data monetization strategy and implementation plan',
          riskMitigation: 'Data governance, privacy compliance, value realization tracking',
          painPointWeight: 8,
          symptoms: ['Unanalyzed customer data', 'Untapped operational data', 'No data products', 'Limited analytics'],
          quantification: "Data asset value * monetization rate",
          typicalRange: "15-35% revenue potential"
        }
      }
    },


    supplyChain: {
      id: 'supplyChain',
      name: 'Supply Chain Leakage',
      impact: 'MEDIUM',
      recoverability: 'HIGH',
      description: 'Value loss through supply chain inefficiencies',
      weight: 12,
      totalAddressableValue: "20-35% logistics optimization",
      subTypes: {
        inventoryMismanagement: {
          name: 'Inventory Optimization Gaps & Stock Issues',
          impact: '15-25% carrying costs',
          detection: 'Inventory turnover analysis, stockout costs, demand forecasting accuracy',
          recovery: '20-35% optimization through better planning',
          priority: 'IMMEDIATE',
          businessCase: 'Implement inventory optimization and demand forecasting',
          riskMitigation: 'Inventory management systems, supplier collaboration, buffer optimization',
          painPointWeight: 7,
          symptoms: ['Excess inventory', 'Stockouts', 'Poor demand forecasting', 'Obsolescence'],
          quantification: "(Carrying costs + Stockout costs) * inefficiency factor",
          typicalRange: "15-25% carrying costs"
        },
        logisticsInefficiencies: {
          name: 'Transportation & Logistics Optimization Gaps',
          impact: '10-20% logistics costs',
          detection: 'Route optimization analysis, transportation cost benchmarking, delivery performance',
          recovery: '15-30% cost reduction through logistics optimization',
          priority: 'MEDIUM',
          businessCase: 'Implement logistics optimization and route planning systems',
          riskMitigation: 'Route optimization, carrier management, performance monitoring',
          painPointWeight: 6,
          symptoms: ['Inefficient routes', 'High transportation costs', 'Delivery delays', 'Poor carrier performance'],
          quantification: "Transportation cost * optimization potential",
          typicalRange: "10-20% logistics costs"
        }
      }
    },

    quality: {
      id: 'quality',
      name: 'Quality Management Leakage',
      impact: 'MEDIUM',
      recoverability: 'HIGH',
      description: 'Value loss through quality issues and rework',
      weight: 11,
      totalAddressableValue: "15-30% quality cost reduction",
      subTypes: {
        reworkCosts: {
          name: 'Quality Control & Rework Expenses',
          impact: '5-15% total cost',
          detection: 'Cost of quality analysis, defect tracking, rework time analysis',
          recovery: '15-30% quality cost reduction through prevention',
          priority: 'IMMEDIATE',
          businessCase: 'Implement quality management system and prevention programs',
          riskMitigation: 'Quality standards, automated testing, process controls',
          painPointWeight: 7,
          symptoms: ['High rework rates', 'Customer complaints', 'Warranty claims', 'Process variations'],
          quantification: "Rework time * loaded cost + Material waste + Customer impact",
          typicalRange: "5-15% total cost"
        },
        defectEscapes: {
          name: 'Customer-Facing Defects & Field Failures',
          impact: '10-25% customer satisfaction impact',
          detection: 'Customer complaint analysis, warranty claims, field failure rates',
          recovery: '20-40% defect reduction through quality improvement',
          priority: 'HIGH',
          businessCase: 'Implement robust quality controls and failure prevention',
          riskMitigation: 'Quality gates, testing protocols, customer feedback loops',
          painPointWeight: 8,
          symptoms: ['Customer complaints', 'Warranty costs', 'Brand damage', 'Service calls'],
          quantification: "Defect rate * customer impact cost + Warranty costs",
          typicalRange: "10-25% customer satisfaction impact"
        }
      }
    },

    sustainability: {
      id: 'sustainability',
      name: 'Sustainability Leakage',
      impact: 'MEDIUM',
      recoverability: 'MEDIUM',
      description: 'Value loss through poor sustainability practices',
      weight: 10,
      totalAddressableValue: "10-20% cost reduction through sustainability",
      subTypes: {
        carbonFootprint: {
          name: 'Environmental Impact Costs & Carbon Emissions',
          impact: '2-8% operational costs',
          detection: 'Carbon footprint analysis, energy consumption tracking, waste analysis',
          recovery: '10-20% cost reduction through sustainability',
          priority: 'LONG_TERM',
          businessCase: 'Implement sustainability initiatives and energy efficiency',
          riskMitigation: 'Sustainability framework, energy management, waste reduction',
          painPointWeight: 6,
          symptoms: ['High energy costs', 'Carbon emissions', 'Waste generation', 'Regulatory compliance'],
          quantification: "Energy cost + Carbon cost + Waste cost + Regulatory risk",
          typicalRange: "2-8% operational costs"
        },
        resourceInefficiency: {
          name: 'Resource Waste & Circular Economy Gaps',
          impact: '5-15% material costs',
          detection: 'Resource utilization analysis, waste tracking, circularity assessment',
          recovery: '10-25% cost savings through circular economy',
          priority: 'MEDIUM',
          businessCase: 'Implement circular economy principles and waste reduction',
          riskMitigation: 'Resource optimization, recycling programs, circular design',
          painPointWeight: 5,
          symptoms: ['Material waste', 'Low recycling rates', 'Linear processes', 'Resource inefficiency'],
          quantification: "Material waste cost + Disposal costs - Recovery value",
          typicalRange: "5-15% material costs"
        }
      }
    },

    governance: {
      id: 'governance',
      name: 'Governance Leakage',
      impact: 'MEDIUM',
      recoverability: 'HIGH',
      description: 'Value loss through poor governance and decision-making',
      weight: 9,
      totalAddressableValue: "40-60% faster decision making",
      subTypes: {
        decisionDelays: {
          name: 'Slow Strategic Decision Making & Bureaucracy',
          impact: '25-50% opportunity window loss',
          detection: 'Decision cycle time tracking, opportunity cost analysis, approval process mapping',
          recovery: '40-60% faster decisions through better governance',
          priority: 'HIGH',
          businessCase: 'Implement streamlined governance and decision rights',
          riskMitigation: 'Decision frameworks, escalation processes, authority delegation',
          painPointWeight: 8,
          symptoms: ['Slow approvals', 'Missed opportunities', 'Analysis paralysis', 'Consensus culture'],
          quantification: "Opportunity value * decision delay impact",
          typicalRange: "25-50% opportunity window loss"
        },
        oversightGaps: {
          name: 'Inadequate Risk Oversight & Control Failures',
          impact: '10-30% risk exposure increase',
          detection: 'Risk assessment findings, control failure analysis, audit results',
          recovery: '20-40% risk reduction through better governance',
          priority: 'MEDIUM',
          businessCase: 'Implement robust risk management and control frameworks',
          riskMitigation: 'Risk frameworks, control systems, regular audits',
          painPointWeight: 7,
          symptoms: ['Control failures', 'Audit findings', 'Risk incidents', 'Compliance issues'],
          quantification: "Risk event probability * impact cost + Control costs",
          typicalRange: "10-30% risk exposure increase"
        }
      }
    },

    cultural: {
      id: 'cultural',
      name: 'Cultural Leakage',
      impact: 'HIGH',
      recoverability: 'LOW',
      description: 'Value loss through poor organizational culture',
      weight: 8,
      totalAddressableValue: "40-70% better change adoption",
      subTypes: {
        innovationResistance: {
          name: 'Cultural Resistance to Change & Innovation',
          impact: '30-60% innovation adoption failure',
          detection: 'Change readiness assessment, employee surveys, adoption rate analysis',
          recovery: '40-70% better adoption through cultural change',
          priority: 'LONG_TERM',
          businessCase: 'Implement cultural transformation and change management',
          riskMitigation: 'Change management, communication, training, leadership alignment',
          painPointWeight: 7,
          symptoms: ['Resistance to new ideas', 'Slow adoption of changes', 'Cultural silos', 'Not-invented-here syndrome'],
          quantification: "Innovation value * adoption failure rate",
          typicalRange: "30-60% innovation adoption failure"
        },
        collaborationBarriers: {
          name: 'Siloed Thinking & Cross-functional Friction',
          impact: '20-40% collaboration efficiency loss',
          detection: 'Cross-functional collaboration assessment, silo analysis, communication flow mapping',
          recovery: '25-45% efficiency improvement through collaboration',
          priority: 'MEDIUM',
          businessCase: 'Implement collaboration frameworks and cross-functional teams',
          riskMitigation: 'Collaboration tools, cross-functional teams, shared goals',
          painPointWeight: 6,
          symptoms: ['Departmental silos', 'Poor information sharing', 'Turf wars', 'Duplicate efforts'],
          quantification: "Collaboration inefficiency * employee time * loaded cost",
          typicalRange: "20-40% collaboration efficiency loss"
        }
      }
    },

    brand: {
      id: 'brand',
      name: 'Brand & Reputation Leakage',
      impact: 'HIGH',
      recoverability: 'LOW',
      description: 'Value loss through brand erosion and reputation damage',
      weight: 7,
      totalAddressableValue: "15-30% valuation multiple expansion",
      subTypes: {
        customerTrustErosion: {
          name: 'Customer Trust & Loyalty Degradation',
          impact: '30-60% customer lifetime value reduction',
          detection: 'Customer loyalty metrics, trust indicators, retention analysis',
          recovery: '25-45% loyalty premium through trust building',
          priority: 'HIGH',
          businessCase: 'Implement trust-building initiatives and customer engagement',
          riskMitigation: 'Transparency, customer communication, ethical practices',
          painPointWeight: 8,
          symptoms: ['Privacy breaches', 'Poor transparency', 'Broken promises', 'Negative reviews'],
          quantification: "Customer lifetime value * trust impact * retention risk",
          typicalRange: "30-60% customer lifetime value reduction"
        },
        employerBrandGaps: {
          name: 'Talent Attraction & Retention Brand Issues',
          impact: '40-60% premium talent attraction disadvantage',
          detection: 'Employer brand surveys, talent attraction metrics, employee value proposition analysis',
          recovery: '40-60% premium talent attraction capability',
          priority: 'MEDIUM',
          businessCase: 'Build strong employer brand and employee value proposition',
          riskMitigation: 'Employer branding, candidate experience, employee advocacy',
          painPointWeight: 7,
          symptoms: ['Poor social responsibility', 'Weak employer brand', 'Talent acquisition challenges', 'High recruitment costs'],
          quantification: "Talent premium cost + Recruitment cost increase + Productivity impact",
          typicalRange: "40-60% premium talent attraction disadvantage"
        }
      }
    },
    
	ecosystem: {
      id: 'ecosystem',
      name: 'Ecosystem & Partnership Value Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss through poor ecosystem management and partnership inefficiencies',
      weight: 18,
      totalAddressableValue: "30-70% partnership value realization + $100M-$1B platform potential",
      subTypes: {
        partnershipInefficiencies: {
          name: 'Strategic Alliance Misalignment & Governance Gaps',
          impact: '40-70% partnership value unrealized',
          detection: 'Partnership performance analysis, governance assessment, value realization tracking',
          recovery: '2-3x alliance ROI through better management',
          priority: 'HIGH',
          businessCase: 'Implement partnership governance and value realization framework',
          riskMitigation: 'Partnership governance, regular reviews, value tracking, incentive alignment',
          painPointWeight: 8,
          symptoms: ['Misaligned incentives', 'Poor governance', 'Limited collaboration', 'Value leakage', 'Duplicate efforts'],
          quantification: "Partnership potential value * realization gap * duration",
          typicalRange: "40-70% partnership value unrealized"
        },
        supplierInnovationMissed: {
          name: 'Transactional vs Collaborative Supplier Relationships',
          impact: '25-45% supplier capability untapped',
          detection: 'Supplier capability assessment, innovation contribution analysis, relationship evaluation',
          recovery: '15-30% cost reduction through co-innovation',
          priority: 'MEDIUM',
          businessCase: 'Create collaborative innovation programs with key suppliers',
          riskMitigation: 'Supplier collaboration, innovation partnerships, joint development, shared roadmaps',
          painPointWeight: 7,
          symptoms: ['Transactional relationships', 'Limited innovation sharing', 'Price-focused negotiations', 'Missed capabilities'],
          quantification: "Supplier innovation value * collaboration gap * supplier count",
          typicalRange: "25-45% supplier capability untapped"
        },
        academicResearchDisconnect: {
          name: 'Slow Technology Transfer & Research Commercialization',
          impact: '3-5 year commercialization delay',
          detection: 'Research partnership analysis, technology transfer metrics, commercialization timeline tracking',
          recovery: '50-80% faster research-to-revenue conversion',
          priority: 'MEDIUM',
          businessCase: 'Establish university partnerships and technology transfer programs',
          riskMitigation: 'Research partnerships, IP agreements, commercialization teams',
          painPointWeight: 6,
          symptoms: ['Slow technology transfer', 'Limited university engagement', 'Research silos', 'Commercialization delays'],
          quantification: "Research value * delay cost * success probability",
          typicalRange: "3-5 year commercialization delay"
        },
        platformNetworkEffects: {
          name: 'Platform Strategy & Network Effects Missed',
          impact: '$100M-$1B ecosystem value potential',
          detection: 'Platform readiness assessment, API strategy evaluation, ecosystem positioning analysis',
          recovery: '20-40% revenue diversification through platform strategy',
          priority: 'HIGH',
          businessCase: 'Develop API economy strategy and platform business model',
          riskMitigation: 'API strategy, developer ecosystem, platform governance, ecosystem development',
          painPointWeight: 9,
          symptoms: ['Closed architecture', 'No developer strategy', 'Limited APIs', 'Missed ecosystem value', 'No multi-sided strategy'],
          quantification: "Ecosystem value potential * platform readiness gap * network effect multiplier",
          typicalRange: "$100M-$1B ecosystem value potential"
        },
        marketplaceCreationMissed: {
          name: 'Marketplace Revenue Stream Opportunities',
          impact: 'Complete platform revenue stream missing',
          detection: 'Marketplace opportunity analysis, multi-sided market assessment, platform economics evaluation',
          recovery: '30-60% valuation multiple expansion',
          priority: 'HIGH',
          businessCase: 'Create marketplace platform and multi-sided business model',
          riskMitigation: 'Platform strategy, marketplace design, ecosystem governance',
          painPointWeight: 8,
          symptoms: ['Product-only mindset', 'No multi-sided strategy', 'Limited ecosystem', 'Missed network effects'],
          quantification: "Marketplace TAM * platform capture rate * margin",
          typicalRange: "Complete platform revenue stream"
        }
      }
    },

    organizationalLearning: {
      id: 'organizationalLearning',
      name: 'Organizational Learning & Adaptation Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss through knowledge management failures and slow adaptation',
      weight: 16,
      totalAddressableValue: "40-60% productivity improvement + 25-45% innovation velocity",
      subTypes: {
        tacitKnowledgeLoss: {
          name: 'Expert Knowledge Drain & Institutional Memory Loss',
          impact: '20-40% productivity hit with expert departure',
          detection: 'Knowledge retention analysis, expert dependency mapping, institutional memory assessment',
          recovery: '80-95% knowledge retention through systematic capture',
          priority: 'HIGH',
          businessCase: 'Implement knowledge management and expert retention programs',
          riskMitigation: 'Knowledge capture, mentoring programs, documentation, succession planning',
          painPointWeight: 8,
          symptoms: ['No knowledge capture', 'Poor documentation', 'Expert dependencies', 'Relearning cycles'],
          quantification: "Expert productivity value * retention risk * replacement time",
          typicalRange: "20-40% productivity hit with expert departure"
        },
        lessonsLearnedAmnesia: {
          name: 'Repeated Project Failures & Mistake Recycling',
          impact: '40-60% project success rate impact',
          detection: 'Project post-mortem analysis, failure pattern recognition, lesson application tracking',
          recovery: '40-60% project success rate improvement',
          priority: 'MEDIUM',
          businessCase: 'Create lessons learned repository and institutional learning processes',
          riskMitigation: 'Post-mortem processes, knowledge repositories, learning culture',
          painPointWeight: 7,
          symptoms: ['No institutional memory', 'Same mistakes repeated', 'No post-mortems', 'Limited learning transfer'],
          quantification: "Project failure cost * repeat probability * project volume",
          typicalRange: "40-60% project success rate impact"
        },
        crossPollinationBarriers: {
          name: 'Siloed Innovation & Limited Knowledge Sharing',
          impact: '25-45% innovation velocity reduction',
          detection: 'Cross-functional collaboration assessment, innovation flow analysis, knowledge sharing metrics',
          recovery: '25-45% innovation velocity increase',
          priority: 'MEDIUM',
          businessCase: 'Implement communities of practice and cross-functional collaboration',
          riskMitigation: 'Communities of practice, cross-functional teams, knowledge sharing platforms',
          painPointWeight: 6,
          symptoms: ['Departmental boundaries', 'No communities of practice', 'Limited sharing', 'Duplicate innovation'],
          quantification: "Innovation value * sharing gap * collaboration multiplier",
          typicalRange: "25-45% innovation velocity reduction"
        },
        adaptationVelocityGaps: {
          name: 'Slow Strategic Pivot & Market Response',
          impact: '6-18 month lag vs startups',
          detection: 'Decision cycle time tracking, market response analysis, adaptation capability assessment',
          recovery: '60-80% faster market response',
          priority: 'HIGH',
          businessCase: 'Implement agile decision-making and adaptive strategy processes',
          riskMitigation: 'Decision frameworks, agile processes, market sensing, rapid experimentation',
          painPointWeight: 8,
          symptoms: ['Bureaucratic decision-making', 'Rigid planning', 'Slow response', 'Missed opportunities'],
          quantification: "Opportunity value * response delay * market window",
          typicalRange: "6-18 month lag vs startups"
        },
        technologyAdoptionLag: {
          name: 'Enterprise Technology Adoption Delays',
          impact: '2-4 year enterprise adoption lag',
          detection: 'Technology adoption benchmarks, startup comparison, capability gap analysis',
          recovery: '12-18 month faster emerging tech adoption',
          priority: 'MEDIUM',
          businessCase: 'Create emerging technology adoption and integration capability',
          riskMitigation: 'Technology scouting, pilot programs, skill development, change management',
          painPointWeight: 7,
          symptoms: ['Complex procurement', 'Legacy integration challenges', 'Skill gaps', 'Resistance to new tech'],
          quantification: "Technology advantage value * adoption delay cost * competitive impact",
          typicalRange: "2-4 year enterprise adoption lag"
        }
      }
    },

    strategicOptionality: {
      id: 'strategicOptionality',
      name: 'Strategic Optionality & Future-Proofing Leakage',
      impact: 'VERY HIGH',
      recoverability: 'LOW',
      description: 'Value loss through reduced strategic flexibility and future readiness gaps',
      weight: 15,
      totalAddressableValue: "Existential risk mitigation worth $500M-$1B+",
      subTypes: {
        technologyOptionality: {
          name: 'Vendor Lock-in & Architectural Dependency',
          impact: 'Complete architectural dependency risk',
          detection: 'Vendor concentration analysis, architecture flexibility assessment, lock-in risk evaluation',
          recovery: '30-50% TCO reduction through open standards',
          priority: 'LONG_TERM',
          businessCase: 'Implement open standards and multi-vendor strategy',
          riskMitigation: 'Open architecture, vendor diversification, standards adoption, API-first approach',
          painPointWeight: 8,
          symptoms: ['Single vendor dependencies', 'Proprietary systems', 'Limited integration options', 'High switching costs'],
          quantification: "Vendor concentration risk * switching cost * flexibility value",
          typicalRange: "High strategic risk"
        },
        businessModelOptionality: {
          name: 'Single Revenue Stream Dependency',
          impact: '80-90% revenue risk concentration',
          detection: 'Revenue stream analysis, business model diversity assessment, market disruption risk',
          recovery: '40-60% revenue diversification through experimentation',
          priority: 'HIGH',
          businessCase: 'Develop multiple revenue streams and business model innovation',
          riskMitigation: 'Business model experimentation, revenue diversification, market testing',
          painPointWeight: 9,
          symptoms: ['Single revenue stream', 'No business model innovation', 'Market disruption vulnerability', 'Limited experimentation'],
          quantification: "Revenue concentration risk * disruption probability * impact cost",
          typicalRange: "80-90% revenue risk concentration"
        },
        talentOptionality: {
          name: 'Narrow Skill Sets & Limited Cross-training',
          impact: 'Inability to pivot to new opportunities',
          detection: 'Skills breadth analysis, cross-training assessment, workforce agility evaluation',
          recovery: '25-45% workforce agility through continuous reskilling',
          priority: 'MEDIUM',
          businessCase: 'Implement continuous reskilling and talent mobility programs',
          riskMitigation: 'Skills development, cross-training, talent mobility, learning culture',
          painPointWeight: 7,
          symptoms: ['Narrow skill sets', 'No cross-training', 'Limited mobility', 'Skill obsolescence risk'],
          quantification: "Opportunity cost * skill gap * reskilling time",
          typicalRange: "Strategic pivot capability limitation"
        },
        scenarioPlanningGaps: {
          name: 'Strategic Surprise & Disruption Vulnerability',
          impact: '50-80% worse disruption response',
          detection: 'Scenario planning maturity assessment, war-gaming participation, signal detection capability',
          recovery: '50-80% better disruption response through planning',
          priority: 'MEDIUM',
          businessCase: 'Implement scenario planning and strategic foresight capabilities',
          riskMitigation: 'Scenario planning, war-gaming, signal detection, strategic foresight',
          painPointWeight: 8,
          symptoms: ['No war-gaming', 'Weak signal detection', 'Strategic surprise', 'Poor disruption response'],
          quantification: "Disruption impact * preparedness gap * response effectiveness",
          typicalRange: "50-80% worse disruption response"
        },
        portfolioImbalance: {
          name: 'Core vs Future Resource Allocation',
          impact: '90%+ resources in core business',
          detection: 'Resource allocation analysis, innovation investment tracking, portfolio balance assessment',
          recovery: '20-40% revenue from new sources in 5 years',
          priority: 'HIGH',
          businessCase: 'Reallocate resources to future growth opportunities and innovation',
          riskMitigation: 'Portfolio management, innovation funding, strategic resource allocation',
          painPointWeight: 8,
          symptoms: ['Too much core investment', 'Not enough future bets', 'Resource misallocation', 'Growth stagnation'],
          quantification: "Future growth opportunity value * resource allocation gap",
          typicalRange: "90%+ resources in core business"
        }
      }
    },

    quantum: {
      id: 'quantum',
      name: 'Quantum Value Leakage (Emerging Category)',
      impact: 'VERY HIGH',
      recoverability: 'LOW',
      description: 'Value loss through quantum computing readiness gaps and missed computational advantages',
      weight: 12,
      totalAddressableValue: "50-100x computation advantage in specific domains",
      subTypes: {
        algorithmAwareness: {
          name: 'Quantum Algorithm Understanding Gaps',
          impact: '2-3 years to irrelevance in specific domains',
          detection: 'Quantum algorithm awareness assessment, computational advantage analysis, industry benchmarking',
          recovery: '50-100x computation advantage in specific domains',
          priority: 'LONG_TERM',
          businessCase: 'Develop quantum algorithm expertise and application research',
          riskMitigation: 'Quantum education, research partnerships, algorithm development',
          painPointWeight: 8,
          symptoms: ['No quantum awareness', 'Algorithm knowledge gaps', 'Limited research', 'Technology blindspot'],
          quantification: "Quantum advantage value * readiness gap * time to impact",
          typicalRange: "2-3 years to irrelevance"
        },
        talentPipeline: {
          name: 'Quantum Talent & Skill Development Gaps',
          impact: '3-5 year talent gap',
          detection: 'Quantum talent pipeline analysis, skill gap assessment, educational program evaluation',
          recovery: 'First-mover advantage in quantum-enabled industries',
          priority: 'LONG_TERM',
          businessCase: 'Build quantum talent pipeline and educational partnerships',
          riskMitigation: 'Talent development, university partnerships, skill building',
          painPointWeight: 7,
          symptoms: ['Missing talent pipeline', 'No quantum literacy', 'Skill shortages', 'Limited expertise'],
          quantification: "Talent gap cost * recruitment difficulty * training time",
          typicalRange: "3-5 year talent gap"
        },
        useCaseIdentification: {
          name: 'Quantum Application & Use Case Blindspots',
          impact: 'Missed industry transformation opportunities',
          detection: 'Quantum use case analysis, industry application assessment, competitive quantum initiatives',
          recovery: 'Industry leadership in quantum adoption',
          priority: 'MEDIUM',
          businessCase: 'Identify and develop quantum computing use cases for competitive advantage',
          riskMitigation: 'Use case research, pilot projects, industry collaboration',
          painPointWeight: 7,
          symptoms: ['No use case research', 'Application blindspots', 'Limited experimentation', 'Follower position'],
          quantification: "Industry transformation value * adoption leadership advantage",
          typicalRange: "Missed industry transformation"
        }
      }
    },

    gccSpecific: {
      id: 'gccSpecific',
      name: 'GCC-Specific Strategic Leakage',
      impact: 'HIGH',
      recoverability: 'MEDIUM',
      description: 'Value loss specific to GCC establishment and evolution pathways',
      weight: 14,
      totalAddressableValue: "30-50% GCC value potential unrealized",
      subTypes: {
        costCenterTrap: {
          name: 'Permanent Back-Office Perception',
          impact: 'Limited strategic influence and budget constraints',
          detection: 'Strategic influence assessment, budget allocation analysis, executive perception surveys',
          recovery: 'Shift from cost center to value creator positioning',
          priority: 'HIGH',
          businessCase: 'Implement value demonstration framework and executive storytelling',
          riskMitigation: 'Value metrics, executive communication, strategic project participation',
          painPointWeight: 8,
          symptoms: ['Budget constraints', 'Limited influence', 'Back-office perception', 'Cost focus only'],
          quantification: "Strategic value potential * perception gap * influence limitation",
          typicalRange: "Limited strategic influence"
        },
        innovationCeiling: {
          name: 'Capability Boundaries & Value Contribution Limits',
          impact: '50-80% value potential unrealized',
          detection: 'Capability maturity assessment, innovation metrics, strategic project participation analysis',
          recovery: 'Higher value contribution and strategic partnership',
          priority: 'HIGH',
          businessCase: 'Develop strategic capability roadmap and innovation metrics',
          riskMitigation: 'Capability development, innovation tracking, strategic alignment',
          painPointWeight: 8,
          symptoms: ['Capability boundaries too narrow', 'Missed transformation opportunities', 'Limited innovation', 'Reactive operations'],
          quantification: "Value potential * capability gap * strategic alignment",
          typicalRange: "50-80% value potential unrealized"
        },
        talentCeiling: {
          name: 'Inability to Attract Top-Tier Talent',
          impact: 'Limited value creation potential',
          detection: 'Talent attraction metrics, employer brand assessment, recruitment success rates',
          recovery: 'Enhanced talent acquisition and retention capabilities',
          priority: 'MEDIUM',
          businessCase: 'Build employer brand and career path design for premium talent',
          riskMitigation: 'Employer branding, career development, competitive compensation',
          painPointWeight: 7,
          symptoms: ['Limited value creation', 'Talent attraction challenges', 'Career path limitations', 'Skill gaps'],
          quantification: "Talent quality impact * value creation potential * recruitment success",
          typicalRange: "Limited value creation potential"
        },
        centerOfExcellenceMissed: {
          name: 'Service Delivery Focus vs Expertise Leverage',
          impact: '60-80% expertise leverage unrealized',
          detection: 'Expertise utilization analysis, knowledge sharing assessment, community engagement metrics',
          recovery: 'Center of Excellence establishment and knowledge leadership',
          priority: 'MEDIUM',
          businessCase: 'Create communities of practice and knowledge sharing platforms',
          riskMitigation: 'Community building, knowledge platforms, expertise development',
          painPointWeight: 7,
          symptoms: ['Remaining service delivery focused', 'Limited knowledge sharing', 'No thought leadership', 'Expertise silos'],
          quantification: "Expertise value * leverage gap * community impact",
          typicalRange: "60-80% expertise leverage unrealized"
        },
        globalNetworkIsolation: {
          name: 'Isolated GCC Operations & Duplicated Efforts',
          impact: 'No scale advantages and efficiency losses',
          detection: 'Global collaboration assessment, process standardization analysis, duplication identification',
          recovery: 'Standardized processes and shared service platforms',
          priority: 'MEDIUM',
          businessCase: 'Implement global network optimization and shared services',
          riskMitigation: 'Standardization, collaboration platforms, shared services',
          painPointWeight: 6,
          symptoms: ['Isolated operations', 'Duplicated efforts', 'No scale advantages', 'Inefficient processes'],
          quantification: "Scale advantage value * duplication cost * efficiency loss",
          typicalRange: "No scale advantages"
        }
      }
    },

	// Mandar

    strategicOptionality: {
      id: 'strategicOptionality',
      name: 'Strategic Optionality & Future-Proofing Leakage',
      impact: 'VERY HIGH',
      recoverability: 'LOW',
      description: 'Value loss through reduced strategic flexibility and future readiness gaps',
      weight: 15,
      totalAddressableValue: "Existential risk mitigation worth $500M-$1B+",
      subTypes: {
        technologyOptionality: {
          name: 'Vendor Lock-in & Architectural Dependency',
          impact: 'Complete architectural dependency risk',
          detection: 'Vendor concentration analysis, architecture flexibility assessment, lock-in risk evaluation',
          recovery: '30-50% TCO reduction through open standards',
          priority: 'LONG_TERM',
          businessCase: 'Implement open standards and multi-vendor strategy',
          riskMitigation: 'Open architecture, vendor diversification, standards adoption',
          painPointWeight: 8,
          symptoms: ['Single vendor dependencies', 'Proprietary systems', 'Limited integration options'],
          quantification: "Vendor concentration risk * switching cost",
          typicalRange: "High strategic risk"
        },
        quantumReadiness: {
          name: 'Quantum Computing Preparation Gaps',
          impact: '2-3 years to irrelevance in specific domains',
          detection: 'Quantum algorithm awareness, talent pipeline analysis, use case identification',
          recovery: '50-100x computation advantage in specific domains',
          priority: 'LONG_TERM',
          businessCase: 'Develop quantum readiness strategy and capability building',
          riskMitigation: 'Quantum education, research partnerships, use case development',
          painPointWeight: 7,
          symptoms: ['No quantum awareness', 'Missing talent pipeline', 'No use case research'],
          quantification: "Quantum advantage value * readiness gap",
          typicalRange: "First-mover advantage potential"
        }
      }
    }
  },

  // Enhanced prioritization framework
  prioritizationMatrix: {
    immediateFocus: {
      criteria: "HIGH IMPACT + HIGH RECOVERABILITY",
      categories: ['financial.duplicateSystems', 'financial.underutilizedLicenses', 'strategic.competitiveBlindspots', 'digital.dataMonetizationMissed'],
      valuePotential: "$50-150M immediate recoverable value",
      timeframe: "0-6 months"
    },
    strategicInvestments: {
      criteria: "HIGH IMPACT + LOW RECOVERABILITY", 
      categories: ['strategicOptionality.technologyOptionality', 'cultural.innovationResistance', 'ecosystem.platformOpportunities'],
      valuePotential: "$200-500M long-term strategic value",
      timeframe: "18-36 months"
    },
    quickWins: {
      criteria: "LOW IMPACT + HIGH RECOVERABILITY",
      categories: ['process.manualWorkflows', 'financial.inefficientProcurement', 'operational.meetingEfficiency'],
      valuePotential: "$10-50M rapid value recovery",
      timeframe: "0-3 months"
    },
    monitorOnly: {
      criteria: "LOW IMPACT + LOW RECOVERABILITY",
      categories: ['quality.minorVariations', 'process.localizedInefficiencies'],
      valuePotential: "Minimal value, high effort",
      timeframe: "Ongoing monitoring"
    }
  },

  // Comprehensive value quantification model
  quantificationModels: {
    directFinancial: {
      duplicateSystems: {
        formula: "(Redundant licenses + Maintenance) * Systems count",
        variables: ["license_costs", "maintenance_fees", "system_count"],
        typicalRange: "$500K - $5M"
      },
      processInefficiencies: {
        formula: "(FTE hours wasted * loaded cost) + Opportunity cost", 
        variables: ["wasted_hours", "loaded_cost_rate", "opportunity_cost_rate"],
        typicalRange: "$1M - $8M"
      },
      revenueLeakage: {
        formula: "(Theoretical max revenue - Actual revenue) * Margin",
        variables: ["max_revenue_potential", "actual_revenue", "margin_rate"],
        typicalRange: "3-15% of revenue"
      }
    },
    strategicImpact: {
      slowInnovation: {
        formula: "Market share loss * margin impact",
        variables: ["market_share_loss", "margin_impact", "time_delay"],
        typicalRange: "20-60% market share impact"
      },
      talentTurnover: {
        formula: "(Recruitment + Training + Productivity loss) * turnover rate",
        variables: ["recruitment_cost", "training_cost", "productivity_loss", "turnover_rate"],
        typicalRange: "50-200% of annual salary"
      }
    }
  },

  // GCC-specific value leakage analysis
  gccSpecificLeakage: {
    establishmentPhase: {
      wrongLocationSelection: {
        impact: "20-40% setup cost overrun",
        mitigation: "Multi-location evaluation, phased capability rollout",
        detection: "Location analysis, cost benchmarking"
      },
      talentAcquisitionIssues: {
        impact: "30-60% ramp-up time extension", 
        mitigation: "Skills-based hiring, retention bonuses, cultural integration",
        detection: "Time-to-productivity metrics, attrition rates"
      }
    },
    operationalPhase: {
      serviceDeliveryGaps: {
        impact: "15-35% client satisfaction impact",
        mitigation: "SLA compliance tracking, quality audits",
        detection: "SLA metrics, client feedback"
      },
      innovationContributionLimits: {
        impact: "50-80% value potential unrealized",
        mitigation: "Innovation metrics, strategic project participation",
        detection: "Innovation output measurement, strategic impact assessment"
      }
    }
  },

  // Enhanced pain point scoring with strategic weighting
  painPointScoring: {
    impactWeights: {
      'VERY HIGH': 10,
      'HIGH': 8,
      'MEDIUM': 6,
      'LOW': 4
    },
    recoverabilityWeights: {
      'HIGH': 10,
      'MEDIUM': 7, 
      'LOW': 4
    },
    priorityWeights: {
      'IMMEDIATE': 10,
      'HIGH': 8,
      'MEDIUM': 6,
      'LONG_TERM': 4
    },
    strategicImportanceWeights: {
      'EXISTENTIAL': 10,
      'COMPETITIVE_ADVANTAGE': 8,
      'OPERATIONAL_EFFICIENCY': 6,
      'COST_REDUCTION': 4
    }
  },

  // Comprehensive pain point analysis with strategic prioritization
  generateStrategicPainPointSummary: (selectedLeakages, priorities, strategicContext) => {
    if (!selectedLeakages || typeof selectedLeakages !== 'object') {
      return [];
    }
    
    const painPoints = [];
    
    Object.entries(selectedLeakages).forEach(([leakageKey, isSelected]) => {
      if (isSelected) {
        const [categoryId, subTypeId] = leakageKey.split('.');
        const category = COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK.categories[categoryId];
        
        if (!category || !category.subTypes) {
          return;
        }
        
        const subType = category.subTypes[subTypeId];
        const priority = priorities && priorities[leakageKey];
        
        if (subType) {
          // Enhanced scoring with strategic context
          const impactScore = COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK.painPointScoring.impactWeights[category.impact] || 6;
          const recoverabilityScore = COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK.painPointScoring.recoverabilityWeights[category.recoverability] || 6;
          const priorityScore = COMPREHENSIVE_VALUE_LEAKAGE_FRAMEWORK.painPointScoring.priorityWeights[priority] || 6;
          const painWeight = subType.painPointWeight || 5;
          
          // Strategic importance based on business context
          const strategicImportance = strategicContext && strategicContext.criticalAreas.includes(categoryId) ? 8 : 6;
          
          const totalScore = (impactScore + recoverabilityScore + priorityScore + painWeight + strategicImportance) / 5;
          
          painPoints.push({
            id: leakageKey,
            name: subType.name,
            category: category.name,
            impact: category.impact,
            recoverability: category.recoverability,
            priority: priority,
            strategicImportance: strategicImportance,
            painScore: Math.round(totalScore * 10) / 10,
            businessCase: subType.businessCase,
            symptoms: subType.symptoms || [],
            quantifiedImpact: subType.impact,
            recoveryPotential: subType.recovery,
            quantificationModel: subType.quantification,
            typicalRange: subType.typicalRange,
            strategicValue: category.totalAddressableValue
          });
        }
      }
    });
    
    // Sort by comprehensive pain score including strategic importance
    return painPoints.sort((a, b) => {
      // Primary sort by pain score
      if (b.painScore !== a.painScore) {
        return b.painScore - a.painScore;
      }
      // Secondary sort by strategic importance
      return b.strategicImportance - a.strategicImportance;
    });
  },

  // Value recovery roadmap generator
  generateValueRecoveryRoadmap: (prioritizedPainPoints, timelineConstraints) => {
    const roadmap = {
      immediateWins: {
        timeline: "0-6 months",
        initiatives: [],
        totalValue: 0
      },
      mediumTerm: {
        timeline: "6-18 months", 
        initiatives: [],
        totalValue: 0
      },
      longTerm: {
        timeline: "18-36 months",
        initiatives: [],
        totalValue: 0
      }
    };

    prioritizedPainPoints.forEach(painPoint => {
      const initiative = {
        id: painPoint.id,
        name: painPoint.name,
        valuePotential: painPoint.recoveryPotential,
        businessCase: painPoint.businessCase,
        strategicImpact: painPoint.strategicImportance
      };

      // Assign to appropriate timeline based on priority and complexity
      if (painPoint.priority === 'IMMEDIATE' && painPoint.recoverability === 'HIGH') {
        roadmap.immediateWins.initiatives.push(initiative);
        roadmap.immediateWins.totalValue += this.estimateValue(painPoint.quantifiedImpact);
      } else if (painPoint.priority === 'HIGH' || painPoint.strategicImportance >= 8) {
        roadmap.mediumTerm.initiatives.push(initiative);
        roadmap.mediumTerm.totalValue += this.estimateValue(painPoint.quantifiedImpact);
      } else {
        roadmap.longTerm.initiatives.push(initiative);
        roadmap.longTerm.totalValue += this.estimateValue(painPoint.quantifiedImpact);
      }
    });

    return roadmap;
  },

  // Helper function for value estimation
  estimateValue: (impactString) => {
    // Extract numeric values from impact strings like "$500K - $5M"
    const matches = impactString.match(/\$([\d.]+)([KMB]?)/g);
    if (matches && matches.length > 0) {
      const values = matches.map(match => {
        const value = parseFloat(match.replace(/[$,]/g, ''));
        const multiplier = match.includes('M') ? 1000000 : match.includes('K') ? 1000 : 1;
        return value * multiplier;
      });
      return values.reduce((a, b) => a + b, 0) / values.length;
    }
    return 0;
  }
};

export const ENHANCED_DEFAULT_DRY_RUN_DATA = {
  companyName: "Global Tech Corp",
  industry: "Technology",
  targetGCCSize: 500,
  primaryLocation: "India",
  budgetAllocation: "$50M over 5 years",
  strategicIntent: "Digital Transformation & Innovation",
  totalAddressableValue: "$50-200M+ recoverable value",
  currentState: {
    executiveSponsorship: "Medium",
    digitalMaturity: "Low",
    talentAvailability: "High", 
    marketConditions: "Favorable",
    revenue: 5000000000,
    employeeCount: 25000,
    valueLeakageProfile: {
      financialLeakage: "15-25% of operational costs",
      strategicLeakage: "$200-500M at risk", 
      operationalLeakage: "20-40% efficiency loss"
    }
  },
  strategicContext: {
    criticalAreas: ['strategic', 'digital', 'innovation'],
    transformationUrgency: "HIGH",
    competitivePressure: "INCREASING",
    marketDisruptionRisk: "MEDIUM_HIGH"
  }
};